import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Package, Phone, Mail, ShieldCheck, Sparkles, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductInquiryDialog from '../components/shop/ProductInquiryDialog';
import ProductDetailDialog from '../components/shop/ProductDetailDialog';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { logError } from '../lib/logger';

const API = process.env.REACT_APP_BACKEND_URL;

const ALL = '__all__';

const AVAILABILITY_LABEL = {
  in_stock: 'In Stock',
  low_stock: 'Low Stock',
  out_of_stock: 'Out of Stock',
  coming_soon: 'Coming Soon',
  special_order: 'Special Order',
  call_for_availability: 'Call for Availability',
  hidden: '',
};

const AVAILABILITY_BADGE = {
  in_stock: 'bg-emerald-900/60 text-emerald-300 border-emerald-800',
  low_stock: 'bg-amber-950/60 text-amber-300 border-amber-800',
  out_of_stock: 'bg-slate-800 text-gray-400 border-slate-700',
  coming_soon: 'bg-red-950/60 text-red-300 border-red-800',
  special_order: 'bg-sky-950/60 text-sky-300 border-sky-800',
  call_for_availability: 'bg-slate-800 text-gray-300 border-slate-700',
};

const priceText = (p) => (p == null ? null : `$${Number(p).toFixed(2)}`);

const Shop = () => {
  const { business } = useSiteSettings();
  const [data, setData] = useState({ categories: [], brands: [], products: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState(ALL);
  const [brand, setBrand] = useState(ALL);
  const [avail, setAvail] = useState(ALL);
  const [openProduct, setOpenProduct] = useState(null);
  const [inquiryProduct, setInquiryProduct] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: res } = await axios.get(`${API}/api/catalog/public`);
        if (!cancelled) setData(res || { categories: [], brands: [], products: [] });
      } catch (err) {
        logError('Shop catalog fetch failed', err?.message || err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const catMap = useMemo(() => Object.fromEntries((data.categories || []).map((c) => [c.id, c])), [data.categories]);
  const brandMap = useMemo(() => Object.fromEntries((data.brands || []).map((b) => [b.id, b])), [data.brands]);

  const products = useMemo(() => {
    const list = (data.products || []).filter((p) => p.availability !== 'hidden');
    const term = q.trim().toLowerCase();
    return list.filter((p) => {
      if (cat !== ALL && p.category_id !== cat) return false;
      if (brand !== ALL && p.brand_id !== brand) return false;
      if (avail !== ALL && p.availability !== avail) return false;
      if (!term) return true;
      const hay = [p.name, p.model, p.short_description, p.caliber, brandMap[p.brand_id]?.name, catMap[p.category_id]?.name]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [data.products, q, cat, brand, avail, catMap, brandMap]);

  const featured = products.filter((p) => p.featured).slice(0, 3);

  const resetFilters = () => { setQ(''); setCat(ALL); setBrand(ALL); setAvail(ALL); };
  const activeFilterCount = (cat !== ALL ? 1 : 0) + (brand !== ALL ? 1 : 0) + (avail !== ALL ? 1 : 0) + (q ? 1 : 0);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="shop-page">
      <Header />

      {/* Hero band */}
      <section className="pt-28 pb-8 border-b border-slate-800 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.07]"
          style={{
            backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.4) 0 1px, transparent 1px 12px)',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link to="/">
            <Button data-testid="shop-back-home" variant="outline" className="mb-6 bg-slate-900 border-slate-700 text-white hover:bg-slate-800">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-red-400 text-[11px] uppercase tracking-[0.25em] mb-3">
                <ShieldCheck className="w-3.5 h-3.5" /> Curated Inventory
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">Shop</h1>
              <p className="text-gray-400 text-base md:text-lg max-w-2xl mt-3">
                Suppressors, accessories, and gear we stock & recommend. Availability is live — call us any time to
                confirm before you drive out.
              </p>
            </div>
            {business?.phone && (
              <a
                data-testid="shop-hero-call"
                href={`tel:${business.phone}`}
                className="inline-flex items-center gap-2 self-start md:self-end bg-red-600 hover:bg-red-700 text-white px-5 py-3 rounded-md font-medium transition-colors"
              >
                <Phone className="w-4 h-4" /> {business.phone}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Featured strip */}
      {!loading && featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-2 text-amber-400 text-[11px] uppercase tracking-[0.25em] mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Featured
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((p) => (
              <FeaturedCard
                key={`feat-${p.id}`}
                product={p}
                brand={brandMap[p.brand_id]}
                category={catMap[p.category_id]}
                onOpen={() => setOpenProduct(p)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Filter bar */}
      <section className="sticky top-20 z-30 bg-slate-950/95 backdrop-blur-sm border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            {/* Search — always visible */}
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                data-testid="shop-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products, models, calibers…"
                className="pl-9 bg-slate-900 border-slate-700 text-white placeholder:text-gray-500 h-10"
              />
            </div>

            {/* Mobile toggle */}
            <div className="flex md:hidden gap-2">
              <Button
                data-testid="shop-mobile-filter-toggle"
                variant="outline"
                onClick={() => setMobileFiltersOpen((v) => !v)}
                className="flex-1 border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters {activeFilterCount > 0 && <span className="ml-2 bg-red-600 text-white text-xs px-1.5 rounded-full">{activeFilterCount}</span>}
              </Button>
              {activeFilterCount > 0 && (
                <Button data-testid="shop-mobile-reset" variant="outline" onClick={resetFilters} className="border-slate-700 bg-slate-900 text-gray-300 hover:bg-slate-800">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Desktop filters */}
            <div className="hidden md:flex items-center gap-2">
              <FilterSelect testId="shop-filter-category" value={cat} onChange={setCat} label="Category" all="All categories" options={data.categories.map((c) => [c.id, c.name])} />
              <FilterSelect testId="shop-filter-brand" value={brand} onChange={setBrand} label="Brand" all="All brands" options={data.brands.map((b) => [b.id, b.name])} />
              <FilterSelect
                testId="shop-filter-availability"
                value={avail}
                onChange={setAvail}
                label="Availability"
                all="Any availability"
                options={Object.entries(AVAILABILITY_LABEL).filter(([, v]) => v).map(([k, v]) => [k, v])}
              />
              {activeFilterCount > 0 && (
                <Button data-testid="shop-desktop-reset" variant="ghost" onClick={resetFilters} className="text-gray-400 hover:text-white hover:bg-slate-800">
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* Mobile collapsible filters */}
          {mobileFiltersOpen && (
            <div className="md:hidden mt-3 grid grid-cols-1 gap-2 pb-2" data-testid="shop-mobile-filters">
              <FilterSelect testId="shop-mfilter-category" value={cat} onChange={setCat} label="Category" all="All categories" options={data.categories.map((c) => [c.id, c.name])} full />
              <FilterSelect testId="shop-mfilter-brand" value={brand} onChange={setBrand} label="Brand" all="All brands" options={data.brands.map((b) => [b.id, b.name])} full />
              <FilterSelect testId="shop-mfilter-availability" value={avail} onChange={setAvail} label="Availability" all="Any availability" options={Object.entries(AVAILABILITY_LABEL).filter(([, v]) => v).map(([k, v]) => [k, v])} full />
            </div>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={`skel-${i}`} data-testid="shop-skeleton" className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                <Skeleton className="h-56 w-full bg-slate-800" />
                <div className="p-4 space-y-2">
                  <Skeleton className="h-4 w-2/3 bg-slate-800" />
                  <Skeleton className="h-3 w-1/2 bg-slate-800" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <EmptyState onReset={resetFilters} hasFilters={activeFilterCount > 0} />
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-4" data-testid="shop-result-count">
              Showing <span className="text-gray-300 font-semibold">{products.length}</span> product{products.length === 1 ? '' : 's'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="shop-grid">
              {products.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  brand={brandMap[p.brand_id]}
                  category={catMap[p.category_id]}
                  onOpen={() => setOpenProduct(p)}
                  onInquire={() => setInquiryProduct(p)}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <Footer />

      <ProductDetailDialog
        open={Boolean(openProduct)}
        product={openProduct}
        brand={openProduct ? brandMap[openProduct.brand_id] : null}
        category={openProduct ? catMap[openProduct.category_id] : null}
        onClose={() => setOpenProduct(null)}
        onInquire={() => { setInquiryProduct(openProduct); setOpenProduct(null); }}
      />
      <ProductInquiryDialog
        open={Boolean(inquiryProduct)}
        product={inquiryProduct}
        onClose={() => setInquiryProduct(null)}
      />
    </div>
  );
};

// ---- Filter select ----
const FilterSelect = ({ testId, value, onChange, all, options, label, full = false }) => (
  <select
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    aria-label={label}
    className={`bg-slate-900 border border-slate-700 text-white text-sm rounded-md px-3 h-10 focus:outline-none focus:border-red-500 ${full ? 'w-full' : ''}`}
  >
    <option value={ALL}>{all}</option>
    {options.map(([v, l]) => (
      <option key={v} value={v}>{l}</option>
    ))}
  </select>
);

// ---- Product card ----
const ProductCard = ({ product, brand, category, onOpen, onInquire }) => {
  const price = priceText(product.price);
  const sale = priceText(product.sale_price);
  const showSale = sale && product.sale_price != null && product.price != null && product.sale_price < product.price;
  const availClass = AVAILABILITY_BADGE[product.availability] || 'bg-slate-800 text-gray-300 border-slate-700';
  const availLabel = AVAILABILITY_LABEL[product.availability] || product.availability;
  return (
    <div
      data-testid={`shop-card-${product.id}`}
      className="group bg-slate-900 border border-slate-800 rounded-lg overflow-hidden hover:border-red-600/60 transition-all duration-200 flex flex-col"
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid={`shop-card-image-${product.id}`}
        className="relative block w-full h-56 bg-slate-950 overflow-hidden text-left"
        aria-label={`Open ${product.name}`}
      >
        {product.main_image_url ? (
          <img
            src={product.main_image_url}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-700">
            <Package className="w-14 h-14" />
          </div>
        )}
        {product.is_new && (
          <span className="absolute top-2 left-2 text-[10px] tracking-widest uppercase bg-red-600 text-white px-2 py-0.5 rounded">New</span>
        )}
        {availLabel && (
          <span className={`absolute top-2 right-2 text-[10px] tracking-widest uppercase border px-2 py-0.5 rounded ${availClass}`}>
            {availLabel}
          </span>
        )}
      </button>

      <div className="p-4 flex flex-col flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1">
          {brand?.name || 'TayLord Arms'}{category?.name ? ` · ${category.name}` : ''}
        </div>
        <h3 className="text-white font-semibold leading-snug">{product.name}</h3>
        {product.short_description && (
          <p className="text-gray-400 text-sm mt-1 line-clamp-2">{product.short_description}</p>
        )}
        <div className="flex items-end justify-between mt-3">
          <div className="tabular-nums">
            {showSale ? (
              <>
                <span className="text-white font-bold text-lg">{sale}</span>
                <span className="ml-2 text-gray-500 line-through text-sm">{price}</span>
              </>
            ) : price ? (
              <span className="text-white font-bold text-lg">{price}</span>
            ) : (
              <span className="text-gray-500 text-xs">Call for pricing</span>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button
            data-testid={`shop-card-details-${product.id}`}
            variant="outline"
            onClick={onOpen}
            className="flex-1 border-slate-700 bg-slate-800 text-white hover:bg-slate-700 h-9"
          >
            Details
          </Button>
          <Button
            data-testid={`shop-card-inquire-${product.id}`}
            onClick={onInquire}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white h-9"
          >
            <Mail className="w-4 h-4 mr-1.5" /> Inquire
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---- Featured card ----
const FeaturedCard = ({ product, brand, category, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    data-testid={`shop-featured-${product.id}`}
    className="group relative text-left bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 hover:border-amber-500/60 rounded-lg overflow-hidden transition-all"
  >
    <div className="flex gap-3 p-3">
      <div className="w-24 h-24 bg-slate-950 rounded overflow-hidden flex-shrink-0">
        {product.main_image_url ? (
          <img src={product.main_image_url} alt={product.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-700"><Package className="w-8 h-8" /></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Featured</div>
        <div className="text-white font-semibold truncate">{product.name}</div>
        <div className="text-xs text-gray-500 truncate">{brand?.name || ''}{category?.name ? ` · ${category.name}` : ''}</div>
        {product.short_description && <div className="text-xs text-gray-400 mt-1 line-clamp-2">{product.short_description}</div>}
      </div>
    </div>
  </button>
);

// ---- Empty state ----
const EmptyState = ({ onReset, hasFilters }) => (
  <div className="text-center py-16 border border-dashed border-slate-800 rounded-lg" data-testid="shop-empty">
    <Package className="w-10 h-10 text-gray-700 mx-auto mb-3" />
    <p className="text-gray-300 font-medium">No products match your filters yet.</p>
    <p className="text-gray-500 text-sm mt-1">
      {hasFilters ? 'Try loosening your filters — or give us a call and we can point you the right way.' : 'Check back soon — new inventory arrives regularly.'}
    </p>
    {hasFilters && (
      <Button data-testid="shop-empty-reset" onClick={onReset} className="mt-4 bg-red-600 hover:bg-red-700 text-white">
        Reset filters
      </Button>
    )}
  </div>
);

export default Shop;
